import { useState, useCallback, useRef, useEffect } from "react";

const API_BASE = import.meta.env.VITE_WPP_API_BASE as string;

export interface WppLabelItem {
  id: string | { _serialized: string; user?: string; server?: string };
}

export interface WppLabel {
  id: string;
  name: string;
  color: number;
  hexColor?: string;
  count?: number;
  labelItemCollection?: WppLabelItem[] | any[];
  items?: WppLabelItem[] | any[];
  chats?: WppLabelItem[] | any[];
}

export interface ChatLabelState {
  chatId: string;
  stage: PipelineStage | null;
  tags: ExtraTag[];
}

export const PIPELINE_STAGES = ["Lead New", "In Contact", "Negotiating", "Customer", "Lost"] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export const EXTRA_TAGS = ["VIP", "Complaint"] as const;
export type ExtraTag = (typeof EXTRA_TAGS)[number];

const PIPELINE_LABEL_COLORS: Record<string, number> = {
  "Lead New": 4292849392,
  "In Contact": 4282627336,
  "Negotiating": 4294944768,
  "Customer": 4280586496,
  "Lost": 4291547136,
};

const EXTRA_TAG_COLORS: Record<string, number> = {
  VIP: 4294927680,
  Complaint: 4294934528,
};

export function getChatIdsFromLabel(label: WppLabel): string[] {
  if (!label) return [];
  const items =
    (label as any).labelItemCollection ||
    (label as any).items ||
    (label as any).chats ||
    (label as any).labelItems ||
    (label as any).collection ||
    [];
  if (!Array.isArray(items)) return [];

  return items
    .map((item: any) => {
      if (!item) return "";
      if (typeof item === "string") return item;
      if (typeof item === "number") return String(item);
      if (typeof item === "object") {
        if (typeof item.id === "string") return item.id;
        if (item.id && typeof item.id._serialized === "string") return item.id._serialized;
        if (typeof item._serialized === "string") return item._serialized;
        if (item.id && typeof item.id.user === "string") return `${item.id.user}@c.us`;
      }
      return "";
    })
    .filter(Boolean);
}

function getTokenFromStorage(sessionName: string): string | null {
  try {
    const raw = localStorage.getItem(`wpp_session_state_${sessionName}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.status !== "connected") return null;
    return parsed?.wppToken || null;
  } catch {
    return null;
  }
}

export function useWhatsAppLabels(sessionName: string, enabled = true) {
  const [labels, setLabels] = useState<WppLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Track already-tagged chats to avoid duplicate calls during auto-tagging
  const taggedChatsRef = useRef<Set<string>>(new Set());

  const apiHeaders = useCallback((): Record<string, string> => {
    const token = getTokenFromStorage(sessionName) || "";
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [sessionName]);

  // Fetch all labels from WhatsApp (GET /{sessionName}/get-all-labels)
  const fetchLabels = useCallback(async (): Promise<WppLabel[]> => {
    if (!enabled || !sessionName) {
      setLabels([]);
      return [];
    }
    const token = getTokenFromStorage(sessionName);
    if (!token) {
      setLabels([]);
      return [];
    }
    setLoadingLabels(true);
    setLabelError(null);

    try {
      const res = await fetch(`${API_BASE}/${sessionName}/get-all-labels`, {
        headers: apiHeaders(),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 404) {
          setLabels([]);
          setHasFetched(true);
          if (sessionName) {
            localStorage.removeItem(`wpp_session_state_${sessionName}`);
          }
          return [];
        }
        throw new Error(`Failed to get labels (${res.status})`);
      }

      const rawJson: any = await res.json();
      const list: WppLabel[] = Array.isArray(rawJson)
        ? rawJson
        : Array.isArray(rawJson?.response)
        ? rawJson.response
        : Array.isArray(rawJson?.result)
        ? rawJson.result
        : Array.isArray(rawJson?.data)
        ? rawJson.data
        : [];

      const filteredList = list.filter((l) => {
        if (!l || !l.name) return false;
        const lower = l.name.trim().toLowerCase();
        return lower !== "unread" && lower !== "groups";
      });

      setLabels(filteredList);
      setHasFetched(true);
      return filteredList;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch labels";
      setLabelError(msg);
      setHasFetched(true);
      return [];
    } finally {
      setLoadingLabels(false);
    }
  }, [sessionName, enabled, apiHeaders]);

  // Note: fetchLabels should be called explicitly by components (like CrmPipelinePanel) when connected

  // Determine if setup is done (pipeline labels exist)
  const setupDone = hasFetched ? (Array.isArray(labels) && labels.some((l) => l && l.name && PIPELINE_STAGES.includes(l.name as PipelineStage))) : false;

  // Setup Pipeline Labels (One-Time Creation via API)
  const setupPipelineLabels = useCallback(async (): Promise<boolean> => {
    if (!sessionName) return false;
    setSettingUp(true);
    setLabelError(null);

    try {
      const allLabels: string[] = [...PIPELINE_STAGES, ...EXTRA_TAGS];

      for (const name of allLabels) {
        const color = PIPELINE_LABEL_COLORS[name] || EXTRA_TAG_COLORS[name] || 4292849392;
        const res = await fetch(`${API_BASE}/${sessionName}/add-new-label`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ name, options: { labelColor: color } }),
        });

        if (!res.ok) {
          const text = await res.text();
          if (!text.includes("already") && !text.includes("exist")) {
            console.warn(`Failed to create label "${name}": ${res.status} ${text}`);
          }
        }
      }

      await fetchLabels();
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to setup pipeline labels";
      setLabelError(msg);
      return false;
    } finally {
      setSettingUp(false);
    }
  }, [sessionName, apiHeaders, fetchLabels]);

  // Generic apply/remove label (POST /{sessionName}/add-or-remove-label)
  const applyLabel = useCallback(
    async (chatId: string, labelId: string, type: "add" | "remove"): Promise<boolean> => {
      if (!sessionName || !chatId || !labelId) return false;

      try {
        const res = await fetch(`${API_BASE}/${sessionName}/add-or-remove-label`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({
            chatIds: [chatId],
            options: [{ labelId, type }],
          }),
        });

        if (res.ok) {
          await fetchLabels();
        }
        return res.ok;
      } catch (err) {
        console.error("Failed to apply label:", err);
        return false;
      }
    },
    [sessionName, apiHeaders, fetchLabels]
  );

  // Move Customer to a Different Stage
  const moveStage = useCallback(
    async (chatId: string, fromStage: PipelineStage | null, toStage: PipelineStage): Promise<boolean> => {
      if (!sessionName || !chatId) return false;

      let currentLabels = labels.length > 0 ? labels : await fetchLabels();

      const fromLabel = fromStage ? currentLabels.find((l) => l.name === fromStage) : null;
      let toLabel = currentLabels.find((l) => l.name === toStage);

      if (!toLabel) {
        await setupPipelineLabels();
        const refreshed = await fetchLabels();
        toLabel = refreshed.find((l) => l.name === toStage);
      }
      if (!toLabel) return false;

      try {
        const options: Array<{ labelId: string; type: "add" | "remove" }> = [];
        if (fromLabel) {
          options.push({ labelId: fromLabel.id, type: "remove" });
        }
        options.push({ labelId: toLabel.id, type: "add" });

        const res = await fetch(`${API_BASE}/${sessionName}/add-or-remove-label`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ chatIds: [chatId], options }),
        });

        if (res.ok) {
          await fetchLabels();
        }
        return res.ok;
      } catch (err) {
        console.error("Failed to move stage:", err);
        return false;
      }
    },
    [sessionName, labels, fetchLabels, setupPipelineLabels, apiHeaders]
  );

  // Tag a New Customer as "Lead New"
  const tagNewLead = useCallback(
    async (chatId: string): Promise<boolean> => {
      if (!sessionName || !chatId) return false;
      if (taggedChatsRef.current.has(chatId)) return true;

      const currentLabels = labels.length > 0 ? labels : await fetchLabels();
      let leadLabel = currentLabels.find((l) => l.name === "Lead New");

      if (!leadLabel) {
        await setupPipelineLabels();
        const refreshed = await fetchLabels();
        leadLabel = refreshed.find((l) => l.name === "Lead New");
        if (!leadLabel) return false;
      }

      const ok = await applyLabel(chatId, leadLabel.id, "add");
      if (ok) taggedChatsRef.current.add(chatId);
      return ok;
    },
    [sessionName, labels, fetchLabels, setupPipelineLabels, applyLabel]
  );

  // Add Extra Tag (VIP, Complaint, etc.)
  const addExtraTag = useCallback(
    async (chatId: string, tag: ExtraTag): Promise<boolean> => {
      if (!sessionName || !chatId) return false;

      let currentLabels = labels.length > 0 ? labels : await fetchLabels();
      const tagLabel = currentLabels.find((l) => l.name === tag);
      if (!tagLabel) return false;

      return applyLabel(chatId, tagLabel.id, "add");
    },
    [sessionName, labels, fetchLabels, applyLabel]
  );

  const removeExtraTag = useCallback(
    async (chatId: string, tag: ExtraTag): Promise<boolean> => {
      if (!sessionName || !chatId) return false;

      let currentLabels = labels.length > 0 ? labels : await fetchLabels();
      const tagLabel = currentLabels.find((l) => l.name === tag);
      if (!tagLabel) return false;

      return applyLabel(chatId, tagLabel.id, "remove");
    },
    [sessionName, labels, fetchLabels, applyLabel]
  );

  // Compute stage and extra tags for a specific chat ID from current labels
  const getChatStageAndTags = useCallback(
    (chatId: string): { stage: PipelineStage | null; tags: ExtraTag[] } => {
      let stage: PipelineStage | null = null;
      const tags: ExtraTag[] = [];

      for (const label of labels) {
        const chatIds = getChatIdsFromLabel(label);
        const matches = chatIds.some((id) => id === chatId || id.split("@")[0] === chatId.split("@")[0]);
        if (matches) {
          if (PIPELINE_STAGES.includes(label.name as PipelineStage)) {
            stage = label.name as PipelineStage;
          } else if (EXTRA_TAGS.includes(label.name as ExtraTag)) {
            tags.push(label.name as ExtraTag);
          }
        }
      }

      return { stage, tags };
    },
    [labels]
  );

  // Legacy helper signature kept for compatibility
  const getChatLabels = useCallback(
    async (chatId: string): Promise<ChatLabelState> => {
      const { stage, tags } = getChatStageAndTags(chatId);
      return { chatId, stage, tags };
    },
    [getChatStageAndTags]
  );

  // Auto-Stage on Order Detection
  const autoStageOnOrder = useCallback(
    async (chatId: string, orderConfirmed: boolean): Promise<boolean> => {
      if (!sessionName || !chatId) return false;
      const currentStage = getChatStageAndTags(chatId).stage;

      if (orderConfirmed) {
        return moveStage(chatId, currentStage, "Customer");
      }
      return moveStage(chatId, currentStage, "Negotiating");
    },
    [sessionName, getChatStageAndTags, moveStage]
  );

  // Reset / Cleanup
  const deleteLabel = useCallback(
    async (labelId: string): Promise<boolean> => {
      if (!sessionName || !labelId) return false;

      try {
        const res = await fetch(`${API_BASE}/${sessionName}/delete-label/${labelId}`, {
          method: "PUT",
          headers: apiHeaders(),
        });
        if (res.ok) {
          setLabels((prev) => prev.filter((l) => l.id !== labelId));
        }
        return res.ok;
      } catch (err) {
        console.error("Failed to delete label:", err);
        return false;
      }
    },
    [sessionName, apiHeaders]
  );

  const deleteAllLabels = useCallback(async (): Promise<boolean> => {
    if (!sessionName) return false;

    try {
      const res = await fetch(`${API_BASE}/${sessionName}/delete-all-labels`, {
        method: "PUT",
        headers: apiHeaders(),
      });
      if (res.ok) {
        setLabels([]);
        taggedChatsRef.current.clear();
      }
      return res.ok;
    } catch (err) {
      console.error("Failed to delete all labels:", err);
      return false;
    }
  }, [sessionName, apiHeaders]);

  return {
    labels,
    loadingLabels,
    settingUp,
    labelError,
    setupDone,
    hasFetched,
    fetchLabels,
    setupPipelineLabels,
    tagNewLead,
    moveStage,
    applyLabel,
    addExtraTag,
    removeExtraTag,
    getChatStageAndTags,
    getChatLabels,
    autoStageOnOrder,
    deleteLabel,
    deleteAllLabels,
  };
}